import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslationService } from '../../core/services/translation.service';

/**
 * Epoch-ms → "just now" / "5 min ago" / "3 h ago" / "2 d ago", falling back to a short date
 * past a week. Pure, with `now` required and passed explicitly by callers (typically
 * ClockService.now(), a signal wrapping ClockSyncService's drift-corrected clock) rather
 * than read fresh via Date.now() inside transform() -- a value re-read on every call let two
 * evaluations of the same expression, milliseconds apart, straddle a minute boundary and
 * produce different strings, tripping NG0100 (ExpressionChangedAfterItHasBeenChecked).
 * Being pure now, this only re-runs when `value` or `now` actually change.
 */
@Pipe({
  name: 'relativeTime',
  standalone: true,
})
export class RelativeTimePipe implements PipeTransform {
  private translation = inject(TranslationService);

  transform(value: number | null | undefined, now: number): string {
    if (!value) return '';
    const diff = Math.max(0, now - value);
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return this.translation.translate('time.justNow');
    if (minutes < 60) return this.translation.translate('time.minutesAgo', { n: String(minutes) });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.translation.translate('time.hoursAgo', { n: String(hours) });
    const days = Math.floor(hours / 24);
    if (days < 7) return this.translation.translate('time.daysAgo', { n: String(days) });
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
  }
}
